import { DatePicker } from '@/src/shadComponents/ui/DatePicker';
import { Dialog, Transition } from '@headlessui/react';
import { Copy } from 'lucide-react';
import { Fragment, useRef, useState } from 'react';
import useCreateActivation from './hooks/useCreateActivation';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { Button } from '@/src/shadComponents/ui/button';
import { Input } from '@/src/shadComponents/ui/input';
import { Label } from '@/src/shadComponents/ui/label';

interface CloneActivationModalProps {
    id: number;
    onDismiss: (id?: number) => void;
    activation: any;
}

export default function CloneActivationModal({
    id,
    onDismiss,
    activation,
}: CloneActivationModalProps) {
    const cancelButtonRef = useRef(null);
    const [newName, setNewName] = useState('');
    const [newDate, setNewDate] = useState(new Date());
    const cloneButton = useRef<HTMLButtonElement | null>(null);
    const { cloneActivation } = useCreateActivation();

    return (
        <Transition.Root show={true} as={Fragment}>
            <Dialog
                as="div"
                className="relative z-10"
                initialFocus={cancelButtonRef}
                onClose={() => onDismiss()}
                open={true}
            >
                <Transition.Child
                    as={Fragment}
                    enter="ease-out duration-300"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-200"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <div className="fixed inset-0 bg-muted/500 bg-opacity-75 transition-opacity" />
                </Transition.Child>

                <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
                    <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
                        <Transition.Child
                            as={Fragment}
                            enter="ease-out duration-300"
                            enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                            enterTo="opacity-100 translate-y-0 sm:scale-100"
                            leave="ease-in duration-200"
                            leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                            leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                        >
                            <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-background text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg">
                                <div className="bg-background px-4 pb-4 pt-5 sm:p-6 sm:pb-4">
                                    <div className="sm:flex sm:items-start">
                                        <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-primary sm:mx-0 sm:h-10 sm:w-10">
                                            <Copy
                                                className="h-6 w-6 text-primary-foreground"
                                                aria-hidden="true"
                                            />
                                        </div>
                                        <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left">
                                            <h3
                                                className="text-base font-semibold text-foreground"
                                            >
                                                Clone {activation.name}
                                            </h3>
                                            <div className="mt-2">
                                                <p className="text-sm text-muted-foreground">
                                                    Please provide a name and
                                                    date for the new activation.
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="ml-12 mt-2 p-2 space-y-4">
                                        <div className="space-y-2">
                                            <Label htmlFor="name">
                                                Activation Name
                                            </Label>
                                            <Input
                                                id="name"
                                                name="name"
                                                type="text"
                                                required
                                                value={newName}
                                                onChange={(e) =>
                                                    setNewName(
                                                        e.target.value,
                                                    )
                                                }
                                                placeholder="Enter activation name"
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <Label htmlFor="date">
                                                Date
                                            </Label>
                                            <DatePicker
                                                id="date"
                                                date={newDate}
                                                setDate={setNewDate}
                                            />
                                        </div>
                                    </div>
                                </div>
                                <div className="bg-muted/50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6 gap-3">
                                    <Button
                                        type="button"
                                        ref={cloneButton}
                                        onClick={async () => {
                                            if (cloneButton.current) {
                                                cloneButton.current.disabled =
                                                    true;
                                            }
                                            const response =
                                                await cloneActivation(
                                                    id,
                                                    newName,
                                                    format(
                                                        newDate,
                                                        'yyyy-MM-dd',
                                                    ),
                                                );

                                            if (response) {
                                                toast.success(
                                                    'Activation Cloned!',
                                                );

                                                setTimeout(() => {
                                                    onDismiss(response.id);
                                                }, 1000);
                                            }
                                        }}
                                    >
                                        Clone
                                    </Button>
                                    <Button
                                        type="button"
                                        variant="outline"
                                        onClick={() => onDismiss()}
                                        ref={cancelButtonRef}
                                    >
                                        Cancel
                                    </Button>
                                </div>
                            </Dialog.Panel>
                        </Transition.Child>
                    </div>
                </div>
            </Dialog>
        </Transition.Root>
    );
}
